import { AlertTriangleIcon } from '@langchain/untitled-ui-icons';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';

import { Button, Text } from '@/design-system';
import { HostProjectSchema, HostRevisionSchema } from '@/types/schema';

import { ExpandableErrorAlert } from '../ExpandableErrorAlert';
import { useHostCreateRevisionRollbackMutation } from './hooks/hooks';

export function HostRollbackModal({
  hostProject,
  targetRevision,
  isOpen,
  doClose,
  onSuccess,
}: {
  targetRevision: HostRevisionSchema;
  hostProject: HostProjectSchema;
  isOpen: boolean;
  doClose: () => void;
  onSuccess?: () => void;
}) {
  const rollbackMutation = useHostCreateRevisionRollbackMutation(
    hostProject.id,
    targetRevision.id
  );

  const handleRollback = async () => {
    try {
      await rollbackMutation.trigger(null);
      doClose();
      onSuccess?.();
    } catch {
      // Error will be handled by the mutation error state
    }
  };

  return (
    <Modal open={isOpen} onClose={() => doClose()}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        aria-labelledby="alert-dialog-modal-title"
        aria-describedby="alert-dialog-modal-description"
        sx={{ maxWidth: 500 }}
      >
        <div className="flex items-center">
          <AlertTriangleIcon className="mr-2 h-4 w-4" />
          <Text variant="h2" weight="normal">
            Confirmation
          </Text>
        </div>
        <div className="mt-3 border"></div>
        <div className="mb-6 mt-3 flex flex-col gap-2">
          <p className="text-tertiary">
            If you have updated your state schema, checkpoints with the updated
            schema will not work once you rollback.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {rollbackMutation.error && (
            <ExpandableErrorAlert error={rollbackMutation.error} />
          )}

          <div className="flex justify-end gap-2">
            <Button
              size="md"
              variant="outlined"
              color="secondary"
              onClick={() => doClose()}
              disabled={rollbackMutation.isMutating}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleRollback();
              }}
              loading={rollbackMutation.isMutating}
            >
              Rollback
            </Button>
          </div>
        </div>
      </ModalDialog>
    </Modal>
  );
}
